/**
 * L'ÉCHANGE, DÉCOUPÉ EN TEMPS — de quoi le regarder au lieu de le lire après coup.
 *
 * LE DÉFAUT QU'IL RÉPARE, ET IL A ÉTÉ VU EN JOUANT : « j'ai affronté deux goules et suis mort en
 * deux coups, pas eu le temps de comprendre ». Un coup joué produit d'un bloc quatre à dix
 * événements — le héros frappe, chacun riposte, les états tiquent, quelqu'un tombe — et ces lignes
 * s'empilaient au bas d'une colonne qui défile pendant que l'œil était resté sur le bouton. On
 * lisait le résultat, jamais l'échange.
 *
 * UN TEMPS = UNE LIGNE QU'ON MONTRE, avec ce qu'il faut pour la comprendre : le numéro de tour
 * quand il s'ouvre, et LE COMPTE DES DÉGÂTS SUBIS depuis le début de l'échange. Ce compte est la
 * seule chose que le récit ne disait pas et qui manquait vraiment : trois coups à sept dégâts sur
 * un héros à vingt points de vie, ce n'est pas trois lignes, c'est une mort qui arrive.
 *
 * FONCTION PURE, SANS REACT ET SANS DOM. C'est ce qui la rend testable, et elle l'est : la fenêtre
 * qui l'affiche n'a plus qu'à compter jusqu'à `temps.length`.
 */
import type { EvenementCombat } from "../api/types.js";
import { composeLeRecit, ID_DU_HEROS, type LigneDeRecit, type Noms } from "./combat.js";

export interface Temps {
  ligne: LigneDeRecit;
  /** Le tour qui s'ouvre avec ce temps-là, s'il s'en ouvre un. L'intertitre n'est pas un temps à
   * lui seul : cliquer « Suite » pour lire « Tour 3 » et rien d'autre est un clic volé. */
  tour: number | null;
  /** Les dégâts encaissés par le héros DEPUIS LE DÉBUT DE L'ÉCHANGE, cumulés jusqu'ici. */
  subis: number;
  /** Ce temps-là est celui où le héros tombe. La fenêtre ne se referme pas dessus toute seule. */
  mortel: boolean;
}

/** Ce qu'un événement retire au héros. Le récit le dit en toutes lettres ; ici on l'additionne. */
function degatsSurLeHeros(evenement: EvenementCombat): number {
  if (evenement.type === "attaque" && evenement.cible === ID_DU_HEROS && evenement.touche && !evenement.esquive?.reussie) {
    return evenement.degats;
  }
  if (evenement.type === "etat_tick" && evenement.cible === ID_DU_HEROS) return evenement.degats;
  return 0;
}

/**
 * LES TEMPS D'UN ÉCHANGE. On recompose le récit événement par événement plutôt que d'un coup :
 * c'est le seul moyen de savoir QUELLE ligne vient de QUEL événement, donc d'attacher à chacune son
 * compte de dégâts. `composeLeRecit` reste la seule source des phrases — deux façons d'écrire le
 * même coup finiraient par se contredire, et c'est la contradiction qu'un joueur remarque.
 */
export function tempsDe(evenements: EvenementCombat[], noms: Noms): Temps[] {
  const temps: Temps[] = [];
  let subis = 0;
  let tourEnAttente: number | null = null;
  let dernierTour: number | null = null;

  for (const evenement of evenements) {
    subis += degatsSurLeHeros(evenement);
    const mortel = evenement.type === "chute" && evenement.cible === ID_DU_HEROS;

    for (const ligne of composeLeRecit([evenement], noms)) {
      // Un intertitre de tour attend la ligne suivante et voyage avec elle.
      if (ligne.ouvreLeTour !== undefined) {
        if (ligne.ouvreLeTour !== dernierTour) {
          dernierTour = ligne.ouvreLeTour;
          tourEnAttente = ligne.ouvreLeTour;
        }
        continue;
      }
      temps.push({ ligne, tour: tourEnAttente, subis, mortel });
      tourEnAttente = null;
    }
  }
  return temps;
}
